import {
  Avatar,
  Box,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  HStack,
  Spinner,
  Text,
} from "@chakra-ui/react";
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getDay,
  isToday,
  parseISO,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import apiClient from "services/ApiClient";
import { API_ROUTES, INTERNAL_TEAM_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import MeetingEventPreview from "features/MeetingEvents/Components/MeetingEventPreview";
import { useMeetingEventDetailQuery } from "features/MeetingEvents/hooks";
import { AdminMeetingTypeTag } from "design-system";
import NewHireStackedLabel from "features/NewHireJourney/Components/NewHireStackedLabel";

interface CalendarMeeting {
  id: string;
  date: string;
  title: string;
  startTime: string;
  meetingType: string;
  newHireNames: string[];
  attendeeNames: string[];
}

interface CalendarStartDate {
  date: string;
  journeyId: string;
  newHireName: string | null;
  title: string | null;
}

interface CalendarData {
  meetings: CalendarMeeting[];
  startDates: CalendarStartDate[];
}

type GridDay = { date: Date; dateStr: string; isCurrentMonth: boolean };

function buildCalendarGrid(month: Date, weekdaysOnly: boolean, startOnSunday: boolean): GridDay[] {
  const start = startOfMonth(month);
  const end = endOfMonth(month);
  const days: GridDay[] = [];

  if (weekdaysOnly) {
    const startDow = getDay(start);
    const daysBack = startDow === 0 ? 6 : startDow - 1;
    const endDow = getDay(end);
    const daysForward = endDow === 0 ? 5 : endDow === 6 ? 6 : 5 - endDow;
    const gridEnd = addDays(end, daysForward);

    let curr = subDays(start, daysBack);
    while (curr <= gridEnd) {
      const dow = getDay(curr);
      if (dow >= 1 && dow <= 5) {
        days.push({ date: curr, dateStr: format(curr, "yyyy-MM-dd"), isCurrentMonth: curr >= start && curr <= end });
      }
      curr = addDays(curr, 1);
    }
  } else {
    const firstDow = startOnSunday ? 0 : 1;
    const startDow = getDay(start);
    const daysBack = (startDow - firstDow + 7) % 7;

    for (let i = daysBack - 1; i >= 0; i--) {
      const date = subDays(start, i + 1);
      days.push({ date, dateStr: format(date, "yyyy-MM-dd"), isCurrentMonth: false });
    }

    let curr = start;
    while (curr <= end) {
      days.push({ date: curr, dateStr: format(curr, "yyyy-MM-dd"), isCurrentMonth: true });
      curr = addDays(curr, 1);
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = addDays(end, i);
      days.push({ date, dateStr: format(date, "yyyy-MM-dd"), isCurrentMonth: false });
    }
  }

  return days;
}

function MeetingBadge({ count, todayInverted = false }: { count: number; todayInverted?: boolean }) {
  const bg = todayInverted ? "rgba(100,150,230,0.35)" : "#E6EFFE";
  const color = todayInverted ? "white" : "#2D5DB8";

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      px="5px"
      py="1px"
      borderRadius="3px"
      fontSize="8px"
      fontWeight="700"
      bg={bg}
      color={color}
    >
      {count}
    </Box>
  );
}

function DayCell({
  date,
  dateStr,
  isCurrentMonth,
  meetingCount,
  hasStart,
  isSelected,
  isTodayDate,
  onClick,
}: {
  date: Date;
  dateStr: string;
  isCurrentMonth: boolean;
  meetingCount: number;
  hasStart: boolean;
  isSelected: boolean;
  isTodayDate: boolean;
  onClick: () => void;
}) {
  const bg = isTodayDate ? "brand.500" : isSelected ? "rgba(91,80,214,0.08)" : "transparent";

  const outline = isSelected && !isTodayDate ? "1.5px solid rgba(91,80,214,0.25)" : undefined;

  const numberColor = !isCurrentMonth ? "gray.300" : isTodayDate ? "white" : "content.primary";

  return (
    <Box
      as={isCurrentMonth ? "button" : "div"}
      onClick={isCurrentMonth ? onClick : undefined}
      py="4px"
      px="2px"
      minH="42px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap="3px"
      borderRadius="7px"
      bg={bg}
      outline={outline}
      cursor={isCurrentMonth ? "pointer" : "default"}
      _hover={isCurrentMonth && !isTodayDate ? { bg: "bg.tertiary" } : undefined}
      transition="background 0.12s"
      w="100%"
    >
      <Text fontSize="11px" fontWeight={isTodayDate || isSelected ? "600" : "500"} color={numberColor} lineHeight="1.2">
        {date.getDate()}
      </Text>
      <HStack spacing="2px" justify="center" flexWrap="wrap">
        {meetingCount > 0 && <MeetingBadge count={meetingCount} todayInverted={isTodayDate} />}
        {hasStart && (
          <Text fontSize="9px" lineHeight="1" style={isTodayDate ? { filter: "brightness(10)" } : undefined}>
            ⭐
          </Text>
        )}
      </HStack>
    </Box>
  );
}

function MeetingRow({ meeting, onClick }: { meeting: CalendarMeeting; onClick: () => void }) {
  const time = format(parseISO(meeting.startTime), "h:mm a");
  const title = meeting.newHireNames.length === 1 ? `${meeting.title} — ${meeting.newHireNames[0]}` : meeting.title;

  return (
    <Flex
      as="button"
      align="flex-start"
      gap="6px"
      py="3px"
      w="100%"
      textAlign="left"
      onClick={onClick}
      _hover={{ opacity: 0.75 }}
      transition="opacity 0.12s"
    >
      <Box w="7px" h="7px" borderRadius="full" bg="#3A6EE0" flexShrink={0} mt="5px" />
      <Box flex="1" minW={0}>
        <Text fontSize="12px" fontWeight="500" noOfLines={1}>
          {title}
        </Text>
        <HStack spacing="4px" mt="1px" flexWrap="wrap">
          <Text fontSize="10px" color="content.tertiary">
            {time}
          </Text>
          {meeting.newHireNames.length > 1 && (
            <Text fontSize="10px" color="content.tertiary">
              · {meeting.newHireNames.length} new hires
            </Text>
          )}
          {meeting.attendeeNames.length > 0 && (
            <HStack spacing="1px">
              {meeting.attendeeNames.slice(0, 3).map((name) => (
                <Avatar key={name} name={name} size="2xs" />
              ))}
            </HStack>
          )}
        </HStack>
      </Box>
    </Flex>
  );
}

function MeetingDrawer({ meetingId, isOpen, onClose }: { meetingId: string; isOpen: boolean; onClose: () => void }) {
  const { data: meetingEvent } = useMeetingEventDetailQuery(meetingId, { enabled: isOpen });
  const singleNewHire = meetingEvent?.newHireNames?.length === 1 ? meetingEvent.newHireNames[0] : null;

  return (
    <Drawer size="md" isOpen={isOpen} onClose={onClose} placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" pb={3}>
          <Flex direction="column" pr={6}>
            <HStack spacing="2" align="center">
              {meetingEvent && (
                <Box flexShrink={0}>
                  <AdminMeetingTypeTag.IconBadge meetingType={meetingEvent.meetingType} />
                </Box>
              )}
              <Text fontSize="16px" fontWeight="700" color="content.primary" noOfLines={2}>
                {meetingEvent?.title ?? "Meeting"}
              </Text>
            </HStack>
            <Box
              as={Link}
              to={SHARED_ROUTES.meetingEvents.show.replace(":id", meetingId)}
              fontSize="12px"
              fontWeight="500"
              color="brand.500"
              _hover={{ textDecoration: "underline" }}
              mt="2px"
              display="inline-block"
            >
              View details
            </Box>
          </Flex>
          {singleNewHire && (
            <Box mt="10px">
              <NewHireStackedLabel name={singleNewHire} label="New Hire" />
            </Box>
          )}
        </DrawerHeader>
        <DrawerBody py={4}>
          {meetingEvent && <MeetingEventPreview meetingEvent={meetingEvent} showHeader={false} />}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function DayDetail({
  dateStr,
  meetings,
  starts,
  onMeetingClick,
}: {
  dateStr: string;
  meetings: CalendarMeeting[];
  starts: CalendarStartDate[];
  onMeetingClick: (id: string) => void;
}) {
  const date = parseISO(dateStr);
  const isTodayDate = isToday(date);

  const sessions = meetings.filter(
    (m) => m.meetingType === "group_session_adhoc" || m.meetingType === "group_session_recurring",
  );
  const individual = meetings.filter((m) => m.meetingType === "individual");

  return (
    <Box mt="8px" pt="8px" borderTop="0.5px solid" borderColor="gray.100">
      <Text fontSize="14px" fontWeight="600">
        {format(date, "MMMM d")}
      </Text>
      {isTodayDate && (
        <Text fontSize="12px" color="content.tertiary">
          Today
        </Text>
      )}

      {meetings.length === 0 && starts.length === 0 && (
        <Text fontSize="12px" color="content.tertiary" mt="2" textAlign="center" py="3">
          No meetings or starts
        </Text>
      )}

      {starts.length > 0 && (
        <Box mt="6px" border="1px solid" borderColor="#FDE68A" borderRadius="8px" overflow="hidden">
          <Box bg="#FFFBEB" px="10px" py="6px">
            <HStack spacing="4px">
              <Text fontSize="10px" lineHeight="1">
                ⭐
              </Text>
              <Text fontSize="10px" fontWeight="700" letterSpacing="0.05em" textTransform="uppercase" color="#92600A">
                {isTodayDate ? "Starting Today" : "Starting"}
              </Text>
            </HStack>
          </Box>
          <Box bg="white" px="10px" py="6px">
            {starts.map((s, idx) => {
              const hirePath = INTERNAL_TEAM_ROUTES.newHireJourneys.show.replace(":id", String(s.journeyId));
              return (
                <Flex key={idx} as={Link} to={hirePath} align="center" gap="8px" py="4px" _hover={{ opacity: 0.8 }}>
                  <Avatar name={s.newHireName ?? ""} size="xs" flexShrink={0} />
                  <Box minW={0}>
                    <Text fontSize="12px" fontWeight="500" noOfLines={1}>
                      {s.newHireName ?? "New hire"}
                    </Text>
                    {s.title && (
                      <Text fontSize="10px" color="content.tertiary" noOfLines={1}>
                        {s.title}
                      </Text>
                    )}
                  </Box>
                </Flex>
              );
            })}
          </Box>
        </Box>
      )}

      {sessions.length > 0 && (
        <Box mt="6px" border="1px solid" borderColor="#C7D7F8" borderRadius="8px" overflow="hidden">
          <Box bg="#EEF2FF" px="10px" py="6px">
            <Text fontSize="10px" fontWeight="700" letterSpacing="0.05em" textTransform="uppercase" color="#2D5DB8">
              Sessions & Recurring
            </Text>
          </Box>
          <Box bg="white" px="10px" py="6px">
            {sessions.map((m) => (
              <MeetingRow key={m.id} meeting={m} onClick={() => onMeetingClick(m.id)} />
            ))}
          </Box>
        </Box>
      )}

      {individual.length > 0 && (
        <Box mt="6px" border="1px solid" borderColor="#C7D7F8" borderRadius="8px" overflow="hidden">
          <Box bg="#EEF2FF" px="10px" py="6px">
            <Text fontSize="10px" fontWeight="700" letterSpacing="0.05em" textTransform="uppercase" color="#2D5DB8">
              Individual Meetings
            </Text>
          </Box>
          <Box bg="white" px="10px" py="6px">
            {individual.map((m) => (
              <MeetingRow key={m.id} meeting={m} onClick={() => onMeetingClick(m.id)} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const DOW_7_SUN = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DOW_7_MON = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const DOW_5 = ["MON", "TUE", "WED", "THU", "FRI"];

function NavButton({
  onClick,
  children,
  w,
  fontSize = "11px",
}: {
  onClick: () => void;
  children: React.ReactNode;
  w?: string;
  fontSize?: string;
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      bg="none"
      border="0.5px solid"
      borderColor="gray.200"
      borderRadius="6px"
      h="24px"
      w={w}
      px={w ? undefined : "6px"}
      cursor="pointer"
      fontSize={fontSize}
      color="content.tertiary"
      display="flex"
      alignItems="center"
      justifyContent="center"
      _hover={{ bg: "bg.tertiary" }}
      whiteSpace="nowrap"
    >
      {children}
    </Box>
  );
}

function SegmentedControl({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <HStack spacing="0" border="0.5px solid" borderColor="gray.200" borderRadius="6px" overflow="hidden">
      {options.map((opt) => (
        <Box
          key={opt.value}
          as="button"
          onClick={() => onChange(opt.value)}
          h="24px"
          px="7px"
          fontSize="10px"
          fontWeight={opt.value === value ? "600" : "400"}
          color={opt.value === value ? "content.primary" : "content.tertiary"}
          bg={opt.value === value ? "bg.tertiary" : "none"}
          cursor="pointer"
          _hover={{ bg: "bg.tertiary" }}
        >
          {opt.label}
        </Box>
      ))}
    </HStack>
  );
}

export default function CalendarCard() {
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<string | null>(() => format(new Date(), "yyyy-MM-dd"));
  const [weekdaysOnly, setWeekdaysOnly] = useState(() => localStorage.getItem("calendarWeekdaysOnly") === "true");
  const [startOnSunday, setStartOnSunday] = useState(() => localStorage.getItem("calendarStartOnSunday") === "true");

  const monthStr = format(currentMonth, "yyyy-MM");
  const todayStr = format(new Date(), "yyyy-MM-dd");

  const { data, isLoading } = useQuery<CalendarData>({
    queryKey: ["dashboardCalendar", monthStr],
    queryFn: () => apiClient.get(`${API_ROUTES.dashboard.calendar}?month=${monthStr}`),
    staleTime: 1000 * 60 * 3,
  });

  const gridDays = buildCalendarGrid(currentMonth, weekdaysOnly, startOnSunday);
  const dowLabels = weekdaysOnly ? DOW_5 : startOnSunday ? DOW_7_SUN : DOW_7_MON;
  const gridColumns = weekdaysOnly ? 5 : 7;

  const meetingsByDate = useMemo(() => {
    const map: Record<string, CalendarMeeting[]> = {};
    for (const m of data?.meetings ?? []) {
      map[m.date] = [...(map[m.date] ?? []), m];
    }
    return map;
  }, [data?.meetings]);

  const startsByDate = useMemo(() => {
    const map: Record<string, CalendarStartDate[]> = {};
    for (const s of data?.startDates ?? []) {
      map[s.date] = [...(map[s.date] ?? []), s];
    }
    return map;
  }, [data?.startDates]);

  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);

  const totalMeetings = data?.meetings?.length ?? 0;

  const selectedMeetings = (selectedDate && meetingsByDate[selectedDate]) || [];
  const selectedStarts = (selectedDate && startsByDate[selectedDate]) || [];

  function handleNavMonth(direction: 1 | -1) {
    setCurrentMonth((prev) => (direction === 1 ? addMonths(prev, 1) : subMonths(prev, 1)));
    setSelectedDate(null);
  }

  function handleToday() {
    setCurrentMonth(startOfMonth(new Date()));
    setSelectedDate(todayStr);
  }

  function handleWeekdaysOnly(val: string) {
    const next = val === "5d";
    setWeekdaysOnly(next);
    localStorage.setItem("calendarWeekdaysOnly", String(next));
    if (next && selectedDate) {
      const dow = getDay(parseISO(selectedDate));
      if (dow === 0 || dow === 6) setSelectedDate(null);
    }
  }

  function handleStartDay(val: string) {
    const next = val === "sun";
    setStartOnSunday(next);
    localStorage.setItem("calendarStartOnSunday", String(next));
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb="6px">
        <Text fontSize="14px" fontWeight="600">
          {format(currentMonth, "MMMM yyyy")}
        </Text>
        <HStack spacing="4px">
          <NavButton onClick={handleToday}>Today</NavButton>
          <SegmentedControl
            options={[
              { label: "5d", value: "5d" },
              { label: "7d", value: "7d" },
            ]}
            value={weekdaysOnly ? "5d" : "7d"}
            onChange={handleWeekdaysOnly}
          />
          {!weekdaysOnly && (
            <SegmentedControl
              options={[
                { label: "Mon", value: "mon" },
                { label: "Sun", value: "sun" },
              ]}
              value={startOnSunday ? "sun" : "mon"}
              onChange={handleStartDay}
            />
          )}
          <NavButton onClick={() => handleNavMonth(-1)} w="24px" fontSize="13px">
            ‹
          </NavButton>
          <NavButton onClick={() => handleNavMonth(1)} w="24px" fontSize="13px">
            ›
          </NavButton>
        </HStack>
      </Flex>

      {/* Legend */}
      <HStack spacing="10px" mb="8px">
        {totalMeetings > 0 && (
          <HStack spacing="3px">
            <MeetingBadge count={totalMeetings} />
            <Text fontSize="10px" color="content.tertiary">
              meetings
            </Text>
          </HStack>
        )}
        {data?.startDates && data.startDates.length > 0 && (
          <HStack spacing="3px">
            <Text fontSize="10px">⭐</Text>
            <Text fontSize="10px" color="content.tertiary">
              starting
            </Text>
          </HStack>
        )}
      </HStack>

      {/* Day-of-week headers */}
      <Grid templateColumns={`repeat(${gridColumns}, 1fr)`} gap="2px">
        {dowLabels.map((dow) => (
          <Box key={dow} textAlign="center" pb="4px">
            <Text fontSize="9px" fontWeight="600" color="gray.400" letterSpacing="0.04em">
              {dow}
            </Text>
          </Box>
        ))}
      </Grid>

      {/* Calendar grid */}
      {isLoading ? (
        <Center py={8}>
          <Spinner size="sm" />
        </Center>
      ) : (
        <Grid templateColumns={`repeat(${gridColumns}, 1fr)`} gap="2px">
          {gridDays.map(({ date, dateStr, isCurrentMonth }) => (
            <DayCell
              key={dateStr}
              date={date}
              dateStr={dateStr}
              isCurrentMonth={isCurrentMonth}
              meetingCount={meetingsByDate[dateStr]?.length ?? 0}
              hasStart={!!startsByDate[dateStr]?.length}
              isTodayDate={dateStr === todayStr}
              isSelected={selectedDate === dateStr}
              onClick={() => setSelectedDate(dateStr)}
            />
          ))}
        </Grid>
      )}

      {/* Selected day detail */}
      {selectedDate && !isLoading && (
        <DayDetail
          dateStr={selectedDate}
          meetings={selectedMeetings}
          starts={selectedStarts}
          onMeetingClick={setSelectedMeetingId}
        />
      )}

      {selectedMeetingId && (
        <MeetingDrawer
          meetingId={selectedMeetingId}
          isOpen={!!selectedMeetingId}
          onClose={() => setSelectedMeetingId(null)}
        />
      )}
    </Box>
  );
}
